import React from "react";
import { Link } from "react-router";
import {
  FlatButton,
  RaisedButton,
  IconButton,
  IconMenu,
  MenuItem,
  Popover,
  Menu
} from "material-ui";
import { PopoverAnimationVertical } from "material-ui/Popover";
import moment from "moment-timezone";
import BaseOverlay from "../overlays/base.jsx";
import InjectedTenantHTML from "./injected-tenant-html.jsx";
import { formatPrice, formatInsertables } from "../../util";

import connectToStores from "alt-utils/lib/connectToStores";
import WishlistStore from "../../stores/WishlistStore";
import EventActions from "../../actions/EventActions";
import PassStore from "../../stores/PassStore";
import TicketStore from "../../stores/TicketStore";
import CreditStore from "../../stores/CreditStore";

import Tooltip from "./tooltip.jsx";
import StarButton from "./star-button.jsx";
import VirtualScreeningIcon from "./icons/virtual-screening-icon.jsx";
import AppUtil from "../../utils/AppUtil.js";

class TicketButton extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      menuRevealed: false,
      soldOutOverlayVisible: false,
      boxOfficeOnlyVisible: false,
      preorderedOverlayVisible: false,
      externalTicketsOverlayVisible: false
    };

    this.onSoldOutClick = this.onSoldOutClick.bind(this);
    this._hideSoldOutOverlay = this._hideSoldOutOverlay.bind(this);

    this.onExternalTicketsClick = this.onExternalTicketsClick.bind(this);
    this._hideExternalTicketsOverlay =
      this._hideExternalTicketsOverlay.bind(this);
    this.continueToExternalTickets = this.continueToExternalTickets.bind(this);

    this._makeOrderButton = this._makeOrderButton.bind(this);
    this._hasMenu = this._hasMenu.bind(this);
    this._makeMenu = this._makeMenu.bind(this);

    this.getStyles = this.getStyles.bind(this);

    this.onOrderButtonMenuOpen = this.onOrderButtonMenuOpen.bind(this);
    this.onOrderButtonClick = this.onOrderButtonClick.bind(this);
    this.onQuickOrderClick = this.onQuickOrderClick.bind(this);
    this.onViewEvent = this.onViewEvent.bind(this);

    this.isSundance = this.isSundance.bind(this);
  }

  static get contextTypes() {
    return {
      isLarge: React.PropTypes.bool,
      isEmbed: React.PropTypes.bool,
      router: React.PropTypes.object.isRequired,
      tenant: React.PropTypes.object,
      appSettings: React.PropTypes.object,
      muiTheme: React.PropTypes.object
    };
  }

  static getStores() {
    return [WishlistStore, TicketStore, PassStore];
  }

  static getPropsFromStores() {
    return {
      isWishlisted: WishlistStore.isWishlisted
    };
  }

  componentDidMount() {
    if (this.props.buyingAdditionalTicketsFromMenuOnly) {
      const triggerOpen = () => {
        if (!this.anchorId) {
          setTimeout(() => triggerOpen(), 50);
          return;
        }

        this.onOrderButtonMenuOpen({
          currentTarget: document.getElementById(this.anchorId),
          preventDefault: () => {},
          stopPropagation: () => {}
        });
      };
      triggerOpen();
    }
  }

  isSundance() {
    if (this.context && this.context.appSettings && this.context.appSettings.frontend_config) {
      return !!this.context.appSettings.frontend_config.sundance_login; 
    }
    else if (this.context && this.context.tenant) {
      return !!this.context.tenant.sundance_login;
    }
    else {
      return false;
    }
  }

  _pastTense(verb) {
    if (verb.toLowerCase() === "buy") {
      return "bought";
    } else if (verb.toLowerCase() === "rsvp") {
      return "rsvped";
    } else {
      return "ordered";
    }
  }

  _makeWishlistButton(force) {
    if (AppUtil.isSundanceMobileMode(this.context.tenant)) return false;

    let wishlisted = this.props.isWishlisted({
      type: "events",
      id: this.props.event.id
    });

    // No starring if at least one ticket has been purchased
    if (
      !force &&
      ((this.props.event.person && this.props.event.person.ticket_count > 0) ||
        TicketStore.getTicketsByEventId(this.props.event.id).length)
    ) {
      return null;
    }

    return (
      <StarButton
        wishlisted={wishlisted}
        type="events"
        id={this.props.event.id}
      />
    );
  }

  _makeOrderButton(initialProps) {
    let styles = this.getStyles(),
      { style, ...props } = initialProps,
      buttonStyle = Object.assign({}, styles.button, style);

    let isPastAvailabilityWindow = false;
    if (this.props.event.is_virtual) {
      const virtualEvent = this.props.event;
      const isUnlocked = virtualEvent.person
        ? !!virtualEvent.person.available_ticket
        : false;
      // If it is a combo event then allow livestream access even after main watch window has expired.
      const isLivestreamUnlocked = virtualEvent.person
        ? !!virtualEvent.person.available_ticket_livestream &&
          !!virtualEvent.films.find(f => f && f.type === "livestream")
        : false;
      const isInAvailabilityWindow =
        !virtualEvent.start_time ||
        (Date.now() >= new Date(virtualEvent.start_time) &&
          Date.now() < new Date(virtualEvent.end_time));
      isPastAvailabilityWindow =
        virtualEvent.end_time &&
        Date.now() > new Date(virtualEvent.start_time) &&
        Date.now() > new Date(virtualEvent.end_time);
      const isInFuture =
        virtualEvent.start_time &&
        !isInAvailabilityWindow &&
        Date.now() < new Date(virtualEvent.start_time);
      const isPreorderable = virtualEvent.allow_preorder && isInFuture;

      // set virtual event button label
      if (isPreorderable) {
        if (!virtualEvent.tickets_button_label) {
          virtualEvent.tickets_button_label =
            (this.context.tenant.virtual_festival_preorder_text &&
              this.context.tenant.virtual_festival_preorder_text.toUpperCase()) ||
            "PRE-ORDER NOW";
        }
      } else if (!isPreorderable && !isUnlocked && isInFuture) {
        virtualEvent.tickets_button_label = "NOT CURRENTLY AVAILABLE";
      } else if (isUnlocked || isLivestreamUnlocked) {
        if (isInFuture) {
          virtualEvent.tickets_button_label = "PRE-ORDERED SUCCESSFULLY";
        } else {
          virtualEvent.tickets_button_label = "WATCH NOW";
        }
      } else if (isPastAvailabilityWindow) {
        virtualEvent.tickets_button_label = "ALREADY OCCURRED";
      } else {
        if (!virtualEvent.tickets_button_label) {
          virtualEvent.tickets_button_label = "UNLOCK NOW";
        }
      }
      props.label = virtualEvent.tickets_button_label;

      // set virtual event button external_tickets_url where apprpriate
      if (!isPreorderable && !isUnlocked && isInFuture) {
        delete virtualEvent.external_tickets_url;
        virtualEvent.virtual_is_disabled = true;
      } else if (
        !window.EVENTIVE_IS_EVERYWHERE ||
        (!isUnlocked && isLivestreamUnlocked)
      ) {
        virtualEvent.external_tickets_url = virtualEvent.public_url
          ? virtualEvent.public_url
          : initialProps.appSettings
          ? initialProps.appSettings.urls.virtual_festival +
            "/play/" +
            virtualEvent.id
          : null;
      }
    }

    if (this.props.onMarquee) {
      if (moment(this.props.event.end_time).isBefore(Date.now())) {
        return (
          <div
            key="marquee"
            onMouseEnter={() => this.setState({ hovered: true })}
            onMouseLeave={() => this.setState({ hovered: false })}
          >
            <RaisedButton
              key="done"
              style={buttonStyle}
              labelStyle={{ textTransform: "initial" }}
              {...props}
              disabled={true}
            />
            <Tooltip
              show={this.state.hovered}
              label="Already occurred"
              containerStyle={{ bottom: -2 }}
            />
          </div>
        );
      }

      if (
        this.context.appSettings.ticket_sales_cutoff &&
        moment(this.props.event.start_time).diff(moment()) <
          this.context.appSettings.ticket_sales_cutoff
      ) {
        return (
          <div
            key="marquee"
            onMouseEnter={() => this.setState({ hovered: true })}
            onMouseLeave={() => this.setState({ hovered: false })}
          >
            <RaisedButton
              key="done"
              style={buttonStyle}
              labelStyle={{ textTransform: "initial" }}
              {...props}
              disabled={true}
            />
            <Tooltip
              show={this.state.hovered}
              label="Box office only"
              containerStyle={{ bottom: -2 }}
            />
          </div>
        );
      }

      return this._hasMenu() ? (
        this._makeMenu(
          <RaisedButton
            labelStyle={{ textTransform: "initial" }}
            style={buttonStyle}
            {...props}
            onClick={this.onOrderButtonMenuOpen}
          />
        )
      ) : (
        <RaisedButton
          labelStyle={{ textTransform: "initial" }}
          style={{
            ...buttonStyle,
            ...(this.props.useVirtualizedMode ? { pointerEvents: "none" } : {})
          }}
          {...props}
          onClick={this.onOrderButtonClick}
        />
      );
    }

    if (this.props.event.external_tickets_url) {
      let isToEventive =
        this.props.event.external_tickets_url.indexOf(".eventive.org") !== -1 ||
        this.props.event.is_virtual;
      const isDisabled =
        this.props.event.is_virtual && isPastAvailabilityWindow;

      return this.props.event.is_virtual ? (
        <RaisedButton
          key="external"
          style={buttonStyle}
          {...props}
          onClick={
            isToEventive
              ? e => {
                  e.preventDefault();
                  e.stopPropagation();
                  this.continueToExternalTickets();
                }
              : this.onExternalTicketsClick
          }
          disabled={isDisabled ? true : false}
        >
          <VirtualScreeningIcon
            styles={{
              padding: 7,
              paddingLeft: 15,
              marginRight: -16,
              float: "left"
            }}
            color={props.primary === true && !isDisabled ? "#FFF" : null}
            scale="1.1"
            disabled={isDisabled}
            useTooltip={false}
          />
        </RaisedButton>
      ) : (
        <RaisedButton
          key="external"
          style={buttonStyle}
          {...props}
          onClick={
            isToEventive
              ? e => {
                  e.preventDefault();
                  e.stopPropagation();
                  this.continueToExternalTickets();
                }
              : this.onExternalTicketsClick
          }
        />
      );
    }

    if (
      !this.props.event.ticket_buckets.length ||
      (this.props.event.show_nonticketed_instead_of_sold_out &&
        !this._hasMenu() &&
        this.props.event.ticket_buckets.filter(
          b => b.public && !b.applicable_pass_buckets.length
        ).length === 0)
    ) {
      return (
        <RaisedButton
          key="nonticketed"
          style={buttonStyle}
          {...props}
          label={this.props.event.tickets_button_label || "Non-ticketed event"}
          disabled={true}
        />
      );
    }

    if (
      this.props.event.is_dated !== false &&
      moment(this.props.event.end_time).isBefore(Date.now())
    ) {
      return (
        <RaisedButton
          key="done"
          style={buttonStyle}
          {...props}
          label="Already occurred"
          disabled={true}
        />
      );
    }

    const currPasses = Object.values(
      PassStore.getPassesForCurrentEventBucket() || {}
    );

    if (
      !this.props.event.tickets_available ||
      this.context.appSettings.mode === "box_office_only" ||
      (!this._hasMenu() &&
        this.props.event.ticket_buckets
          .filter(b => b.public && !b.applicable_pass_buckets.length)
          .every(b => b.quantity_remaining <= 0)) ||
      ((this.props.event.person
        ? this.props.event.person.passes &&
          this.props.event.person.passes.length
        : currPasses.length) &&
        this.props.event.ticket_buckets
          .filter(
            b =>
              b.public &&
              (!b.applicable_pass_buckets.length ||
                (
                  (this.props.event.person && this.props.event.person.passes) ||
                  currPasses
                ).some(p =>
                  b.applicable_pass_buckets.some(pb => p.pass_bucket.id === pb)
                ))
          )
          .every(b => b.quantity_remaining <= 0)) ||
      this.props.event.ticket_buckets
        .filter(b => b.public)
        .every(b => b.quantity_remaining <= 0)
    ) {
      let props = Object.assign({}, props, { primary: false });
      return (
        <RaisedButton
          key="soldout"
          style={buttonStyle}
          {...props}
          label={
            <span>
              {this.context.tenant.event_sold_out_button_label} (
              <span style={{ textDecoration: "underline" }}>info</span>)
            </span>
          }
          backgroundColor={this.context.muiTheme.raisedButton.disabledColor}
          labelColor={this.context.muiTheme.raisedButton.disabledTextColor}
          onClick={this.onSoldOutClick}
        />
      );
    }

    if (
      !this.props.event.is_virtual &&
      this.context.appSettings.ticket_sales_cutoff &&
      moment(this.props.event.start_time).diff(moment()) <
        this.context.appSettings.ticket_sales_cutoff
    ) {
      let props = Object.assign({}, props, { primary: false });
      return (
        <RaisedButton
          key="cutoff"
          style={buttonStyle}
          {...props}
          label={
            <span>
              Box office only (
              <span style={{ textDecoration: "underline" }}>info</span>)
            </span>
          }
          backgroundColor={this.context.muiTheme.raisedButton.disabledColor}
          labelColor={this.context.muiTheme.raisedButton.disabledTextColor}
          onClick={e => this.onSoldOutClick(e, { isCutoff: true })}
        />
      );
    }

    if (this._hasMenu()) {
      return this._makeMenu(
        <RaisedButton
          style={buttonStyle}
          {...props}
          onClick={this.onOrderButtonMenuOpen}
          disabled={this.props.event.virtual_is_disabled ? true : false}
        />
      );
    } else {
      return (
        <RaisedButton
          key="order"
          style={{
            ...buttonStyle,
            ...(this.props.useVirtualizedMode ? { pointerEvents: "none" } : {})
          }}
          {...props}
          onClick={this.onOrderButtonClick}
          disabled={this.props.event.virtual_is_disabled ? true : false}
        />
      );
    }
  }

  _hasMenu() {
    // Always show credits menu
    if (CreditStore.creditsAvailable()) {
      return true;
    }

    if (this.props.event.disable_pass_quick_order) {
      return false;
    }

    let person = this.props.event.person;
    return (person && person.total_passes_available) || PassStore.hasPasses();
  }

  _makeMenu(iconButtonElement) {
    let styles = this.getStyles(),
      items = [];

    // We haven't loaded the event specifics yet
    if (!this.props.event.person) {
      items = [
        <MenuItem
          key={"loading"}
          disabled={true}
          style={Object.assign({}, styles.menuItem, { cursor: "initial" })}
          primaryText={"Loading available ordering options..."}
        />
      ];
    } else {
      let activePasses = this.props.event.person.passes.filter(pass => {
        if (
          pass.errors &&
          pass.errors.some(e => e.indexOf("deactivated") !== -1)
        ) {
          return false;
        }

        if (pass.pass_bucket && pass.pass_bucket.hide_from_users) {
          return false;
        }

        return true;
      });
      items = activePasses.map(pass => {
        let ticketBucket =
            this.props.event.ticket_buckets
              .slice()
              .sort((a, b) => {
                // If only one of the ticket buckets has tickets remaining, prefer that one
                if (a.quantity_remaining && !b.quantity_remaining) {
                  return -1;
                } else if (!a.quantity_remaining && b.quantity_remaining) {
                  return 1;
                }

                // Otherwise prefer bucket with the lowest pass price
                return (
                  a.pass_adjusted[pass.id].price -
                  b.pass_adjusted[pass.id].price
                );
              })
              .find(b => b.pass_adjusted[pass.id].quantity_available > 0) ||
            this.props.event.ticket_buckets[0],
          passAdjusted = ticketBucket.pass_adjusted[pass.id],
          price = formatPrice(passAdjusted.price);

        if (
          ticketBucket.quantity_remaining <= 0 &&
          (!pass.errors || !pass.errors.length)
        ) {
          pass.is_applicable = false;
          pass.errors = [
            `No more tickets are available for this type of ${this.context.tenant.display_pass}.`
          ];

          // One-click hides the confirm screen, so no chance to change quantity
        } else if (this.context.appSettings.passholder_one_click) {
          passAdjusted.quantity_available = 1;
        }

        let baseMessage = pass.pass_bucket.order_message
          .replace(/{{pass}}/g, "{{temp}}") // hacky
          .replace(/{{passbucket}}/g, "{{tempbucket}}") // hacky
          .replace(/pass/g, this.context.tenant.display_pass);

        let quickOrderLabel = formatInsertables(baseMessage, {
            display_pass: () => this.context.tenant.display_pass,
            quantity: () => (passAdjusted.quantity_available > 1 ? "1+" : 1),
            tickets: () =>
              passAdjusted.quantity_available > 1 ? "tickets" : "ticket",
            temp: () => pass.name,
            tempbucket: () => pass.pass_bucket && pass.pass_bucket.name,
            price: () => price
          }),
          passNameForErrorLabel = this.context.tenant.sundance_login
            ? pass.pass_bucket && pass.pass_bucket.name
            : `"${pass.name}"`,
          text = pass.is_applicable
            ? quickOrderLabel
            : `${passNameForErrorLabel} cannot be used: ${pass.errors.join(
                " "
              )}`;
        return (
          <MenuItem
            key={pass.id}
            disabled={!pass.is_applicable}
            onClick={
              pass.is_applicable
                ? this.onQuickOrderClick.bind(this, pass)
                : undefined
            }
            style={Object.assign(
              {},
              styles.menuItem,
              !pass.is_applicable && { cursor: "initial" }
            )}
            primaryText={text}
          />
        );
      });

      // If not all passes are loaded, just show a message that directs the user to the individual
      // event page to order using passes instead. This prevents having to deal with repopulating the menu.
      if (this.props.event.person.has_more_passes) {
        items = [];
        items.push(
          <MenuItem
            key={"has_more"}
            onClick={this.onViewEvent}
            style={styles.menuItem}
            primaryText={`${this.props.event.person.total_passes_available} ${
              this.context.tenant.display_passes
            } available: To order, use the "${
              this.props.event.tickets_button_label ||
              this.context.tenant.order_tickets_button
            }" button on the event page.`}
          />
        );
      }

      if (
        (this.isSundance() || this.context.tenant.pass_show_buy_additional_tickets_option) &&
        (this.props.event.standalone_ticket_sales_enabled ||
          this.props.event.user_standalone_ticket_sales_enabled)
      ) {
        items.push(
          <MenuItem
            key={"addtl"}
            onClick={this.onOrderButtonClick /* just like a normal order */}
            style={styles.menuItem}
            primaryText={
              this.props.buyingAdditionalTicketsFromMenuOnly
                ? "Buy additional standalone tickets..."
                : "Buy additional tickets..."
            }
          />
        );
      }

      const creditsAvailable = CreditStore.creditsAvailable();
      if (creditsAvailable) {
        if (this.props.event.credits_disabled) {
          items = [
            <MenuItem
              key={"credits"}
              disabled={true}
              style={Object.assign({}, styles.menuItem, { cursor: "initial" })}
              primaryText="Credits: Credits are not applicable to this event."
            />
          ].concat(items);
        } else {
          items = [
            <MenuItem
              key={"credits"}
              onClick={e => this.onOrderButtonClick(e, { credits: true })}
              style={styles.menuItem}
              primaryText={`Credits: Order up to ${creditsAvailable} tickets using credits ($0.00)...`}
            />
          ].concat(items);
        }
      }

      if (items.length === 0) {
        items.push(
          <MenuItem
            key={"none"}
            disabled={true}
            style={Object.assign({}, styles.menuItem, { cursor: "initial" })}
            primaryText="Tickets are not currently available for this event."
          />
        );
      }
    }

    if (this.props.buyingAdditionalTicketsFromMenuOnly) {
      this.props.customActions.forEach(action =>
        items.push(
          <MenuItem
            key={action.label}
            style={{ cursor: "pointer" }}
            primaryText={action.label}
            onClick={action.onClick}
          />
        )
      );
    }

    let sundanceItems;
    if (this.isSundance()) {

      // Sort items by ability to use
      items.sort(function(a,b) {
        if (a.props.disabled && !b.props.disabled) { return 1; }
        else if (!a.props.disabled && b.props.disabled) { return -1; }
        else { return 0; }
      });

      // Add header
      let headerItem = [];
      headerItem.push(
        <MenuItem
          key="header"
          style={{ cursor: "not-allowed", fontWeight: "bold", fontSize: 18, fontFamily: "MonumentGrotesk, sans-serif" }}
          primaryText="Ticket Options"
        />
      )

      sundanceItems = headerItem.concat(items);
    }

    return (
      <div
        style={{
          display: "inline-block",
          ...(this.props.useVirtualizedMode ? { pointerEvents: "none" } : {})
        }}
      >
        {iconButtonElement}
        <Popover
          open={this.state.menuRevealed}
          anchorEl={this.state.anchorEl || this.anchorEl}
          onRequestClose={() => this.setState({ menuRevealed: false })}
          animation={PopoverAnimationVertical}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          // targetOrigin={{ vertical: 'top', horizontal: 'right'}}
        >
          <Menu desktop={true} style={styles.menu}>
            {this.isSundance() ? sundanceItems : items}
          </Menu>
        </Popover>
      </div>
    );

    return (
      <IconMenu
        key="ordermenu"
        iconButtonElement={iconButtonElement}
        menuStyle={styles.menu}
        desktop={true}
        autoWidth={true}
        useLayerForClickAway={true}
        // DISABLED - this causes lots of jank on mobile
        // Means you have to hold down / weird stuff
        // open={this.state.menuRevealed}
        // onRequestChange={(open, reason) => {
        //   this.setState({ menuRevealed: open });

        //   // Clear the no-overwrite hold if the menu is closed for a reason other than a tap
        //   if (!open && reason !== 'itemTap') {
        //     EventActions.clearNoAllOverwrite.defer();
        //   }
        // }}
      >
        {items}
      </IconMenu>
    );
  }

  onViewEvent(e) {
    this.context.router.push(`/schedule/${this.props.event.id}`);
    e.stopPropagation();
    e.preventDefault();
  }

  onOrderButtonMenuOpen(e) {
    e.stopPropagation();
    e.preventDefault();
    this.setState({ anchorEl: e.currentTarget, menuRevealed: true });
    EventActions.fetchOne.defer(this.props.event.id, {
      forceNoAllOverwrite: true
    });
  }

  onOrderButtonClick(e, opts) {
    this.setState({ menuRevealed: false });
    this.context.router.push({
      pathname: this.props.fromFilmId
        ? `/films/${this.props.fromFilmId}/tickets/${this.props.event.id}`
        : this.props.fromSchedule
        ? `/schedule/tickets/${this.props.event.id}`
        : `/schedule/${this.props.event.id}/tickets`,
      state: { fromListing: true, withCredits: opts && opts.credits }
    });
    e.stopPropagation();
    e.preventDefault();
  }

  onQuickOrderClick(pass, e) {
    this.setState({ menuRevealed: false });
    this.context.router.push({
      pathname: this.props.fromSchedule
        ? `/schedule/quick/schedule`
        : `/schedule/${this.props.event.id}/quick/event`,
      query: { event: this.props.event.id, pass: pass.id }
    });
    e.stopPropagation();
    e.preventDefault();
  }

  getStyles() {
    return {
      button: {},
      scheduleButton: {
        verticalAlign: -1,
        marginRight: 7
      },
      multiContainer: this.props.noMultiContainer
        ? {}
        : {
            //width: this.props.inline ? null : 380,
            textAlign: this.props.inline ? null : "right",
            float: !this.props.inline ? "right" : null,
            marginLeft: !this.props.inline ? 10 : null,
            marginBottom: !this.props.inline ? 10 : null
          },
      multiContainerSmall: {
        width: "100%",
        textAlign: "center",
        boxSizing: "border-box",
        padding: 6,
        paddingBottom: 20,
        borderBottom: "1px solid #ccc",
        marginBottom: 20
      },
      orderedButton: {
        //display: 'block',
        //float:
        marginLeft: 7
      },
      menu: {
        width: 600,
        maxWidth: "60vw",
        textAlign: "left",
        fontFamily: this.isSundance() ? "MonumentGrotesk, sans-serif" : null
      },
      additionalActionsMenu: {
        maxWidth: "90vw",
        textAlign: "left"
      },
      menuItem: {
        cursor: "pointer",
        whiteSpace: "inherit",
        lineHeight: "25px",
        minHeight: "inherit",
        paddingTop: 4,
        paddingBottom: 4,
        fontFamily: this.isSundance() ? "MonumentGrotesk, sans-serif" : null
      },
      externalTicketsOverlay: {
        textAlign: "center"
      },
      externalTicketsUrlInfo: {
        fontSize: 11,
        marginTop: 10
      },
      externalTicketsUrl: {
        textDecoration: "underline",
        cursor: "pointer"
      }
    };
  }

  onSoldOutClick(e, { isCutoff } = {}) {
    this.setState({
      soldOutOverlayVisible: true,
      boxOfficeOnlyVisible: !!isCutoff
    });
    e.stopPropagation();
    e.preventDefault();
  }

  _hideSoldOutOverlay() {
    this.setState({ soldOutOverlayVisible: false });
  }

  onExternalTicketsClick(e) {
    this.setState({ externalTicketsOverlayVisible: true });
    e.stopPropagation();
    e.preventDefault();
  }

  _hideExternalTicketsOverlay() {
    this.setState({ externalTicketsOverlayVisible: false });
  }

  continueToExternalTickets() {
    this.setState({ externalTicketsOverlayVisible: false });
    let win = window.open(this.props.event.external_tickets_url, "_blank");
    win.focus();
  }

  render() {
    let styles = this.getStyles(),
      { event, compact, inline, hideButton, onMarquee, appSettings } =
        this.props,
      isLarge = this.context.isLarge,
      onlyWishlist = this.props.onlyWishlist,
      showWishlist = this.props.showWishlist || !window.EVENTIVE_IS_EVERYWHERE,
      Button = compact
        ? FlatButton
        : inline || !isLarge
        ? RaisedButton
        : FlatButton;

    let content,
      buttonEventMode,
      label = this.context.tenant.order_tickets_button;

    if (onlyWishlist) {
      return this._makeWishlistButton(true);
    }

    // Custom free event label if specified
    if (
      this.context.tenant.order_tickets_free_button &&
      event.ticket_buckets.some(b => b.public && (b.price === 0 || !b.price))
    ) {
      label = this.context.tenant.order_tickets_free_button;
    }

    // Custom RSVP-required label if specified
    if (
      this.context.tenant.order_button_show_rsvp_label &&
      event.passholder_ticket_required &&
      event.ticket_buckets.length
    ) {
      label = this.context.tenant.order_button_rsvp_label;
    }

    // Custom event-specific label if specified
    if (event.tickets_button_label) {
      label = event.tickets_button_label;
    }

    // Set mode on button for custom styling (Sundance)
    if (event.is_virtual) {
      buttonEventMode = "virtual";
    } else {
      buttonEventMode = "physical";
    }

    // Marquee mode
    if (onMarquee) {
      label = this.props.forceLabel || event.start_time_label;
      showWishlist = false;
    }

    let tickets = TicketStore.getTicketsByEventId(event.id),
      ticketCount =
        tickets.length || (event.person ? event.person.ticket_count : 0);

    // When virtual, only count active tix
    if (event.is_virtual) {
      ticketCount = tickets.filter(t =>
        t.expires_at
          ? new Date(t.expires_at) > Date.now()
          : new Date(t.unlocked_until) > Date.now()
      ).length;
    }

    if (hideButton) {
      content = [];
    } else if (ticketCount && !this.props.sundanceMode) {
      let inlineLabel = (
          <span>
            {`${ticketCount} `}
            <span className="icon-ticket"></span>
          </span>
        ),
        labelChunks = label.split(" "),
        actionWord = labelChunks[0],
        forceOrderMoreLabel = labelChunks.length === 1 ? label : undefined,
        ticketPhrase = this.context.tenant.hide_ticket_term_in_order_button
          ? ""
          : `ticket${ticketCount !== 1 ? "s" : ""}`;

      if (event.is_virtual && ticketCount) {
        const isInFuture =
          event.is_dated && Date.now() < new Date(event.start_time);
        const disabledBackgroundColor = "rgb(229, 229, 229)";
        const disabledTextColor = "rgba(33, 33, 33, 0.3)";
        content = [
          <Button
            key="button"
            buttonStyle={
              isInFuture
                ? { backgroundColor: disabledBackgroundColor }
                : undefined
            }
            labelStyle={Object.assign(
              {
                marginRight: 0,
                marginLeft: 0,
                verticalAlign: 0
              },
              isInFuture
                ? {
                    color: disabledTextColor
                  }
                : {}
            )}
            primary
            label={
              isInFuture ? (
                <span>
                  Pre-ordered successfully (
                  <span style={{ textDecoration: "underline" }}>info</span>)
                </span>
              ) : (
                "Watch now"
              )
            }
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();

              if (isInFuture) {
                this.setState({ preorderedOverlayVisible: true });
                return;
              }

              if (window.EVENTIVE_IS_EVERYWHERE) {
                this.context.router.push({
                  pathname: `/_virtual_play/${event.id}`,
                  state: { fromTicketButton: true }
                });
              } else {
                event.public_url = event.public_url
                  ? event.public_url
                  : appSettings.urls.virtual_festival + "/play/" + event.id;
                window.open(event.public_url);
              }
            }}
          />
        ];
      } else {
        content = [
          <Button
            key="button"
            style={{ minWidth: 0, borderRadius: "0 2px 2px 0" }}
            buttonStyle={{
              borderRadius: "0 2px 2px 0",
              borderLeft: "1px solid #fff"
            }}
            overlayStyle={{ borderRadius: "0 2px 2px 0" }}
            labelStyle={{ marginRight: 0, marginLeft: 0 }}
            label={
              inline || (!isLarge && !compact)
                ? inlineLabel
                : `${ticketCount} ${ticketPhrase} ${
                    forceOrderMoreLabel ? "" : this._pastTense(actionWord)
                  }`
            }
            primary={!inline || onMarquee}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              if (this.context.isEmbed) {
                window.open(`/tickets/${event.id}`);
              } else {
                this.context.router.push({
                  pathname: `/tickets/${event.id}`,
                  state: { fromTicketButton: true }
                });
              }
            }}
          />,
          this.context.tenant.tickets_show_order_more_button
            ? this._makeOrderButton({
                label: onMarquee
                  ? label
                  : inline && compact
                  ? actionWord
                  : forceOrderMoreLabel || `${actionWord} more`,
                className:
                  "eventive__ticket-button__button eventive__universal_ticket-button__button",
                style: onMarquee
                  ? { borderRadius: "2px 0 0 2px" }
                  : styles.orderedButton,
                buttonStyle: onMarquee
                  ? { borderRadius: "2px 0 0 2px" }
                  : undefined,
                overlayStyle: onMarquee
                  ? { borderRadius: "2px 0 0 2px" }
                  : undefined,
                labelStyle: onMarquee
                  ? {
                      marginLeft: 0,
                      marginRight: 0,
                      textTransform: "lowercase"
                    }
                  : undefined,
                primary: onMarquee,
                appSettings: appSettings
              })
            : null
        ].filter(x => !!x);

        if (onMarquee) {
          content.reverse();
        }
      }
    } else {
      content = [
        this._makeOrderButton({
          label: label,
          primary: true,
          className:
            "eventive__ticket-button__button eventive__universal_ticket-button__button",
          appSettings: appSettings
        })
      ];
    }

    if (showWishlist) {
      content[isLarge ? "unshift" : "push"](this._makeWishlistButton());
    }

    if (
      this.props.sundanceMode &&
      this.props.customActions &&
      this.props.customActions.length &&
      !this.props.hideActions
    ) {
      const iconButtonElement = this.props.sundanceMode ? (
        <FlatButton
          id={(() => {
            if (!this.anchorId) {
              this.anchorId = `eventive-anchor-${Math.random()
                .toString()
                .replace(".", "")}`;
            }
            return this.anchorId;
          })()}
          ref={
            this.props.buyingAdditionalTicketsFromMenuOnly
              ? r => {
                  this.anchorEl = r;
                }
              : undefined
          }
          onClick={
            this.props.buyingAdditionalTicketsFromMenuOnly
              ? this.onOrderButtonMenuOpen
              : undefined
          }
          label={
            <span
              style={{
                display: "inline-block",
                // transform: "translateY(-1px)",
                margin: "0 -12px"
              }}
            >
              <svg
                width="28"
                height="6"
                viewBox="0 0 28 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="3" cy="3" r="3" fill="black" />
                <circle cx="14" cy="3" r="3" fill="black" />
                <circle cx="25" cy="3" r="3" fill="black" />
              </svg>
            </span>
          }
        />
      ) : (
        <FlatButton
          style={{
            clear: "both",
            border: "1px solid",
            lineHeight: "38px"
          }}
          label={
            <span style={{ verticalAlign: 3 }}>
              <span className="icon-cog" style={{ verticalAlign: -2 }}></span>
            </span>
          }
        />
      );

      const additionalButton = (
        <IconMenu
          style={this.props.hideOrderButton ? {} : { marginLeft: 12 }}
          iconButtonElement={iconButtonElement}
          menuStyle={styles.additionalActionsMenu}
          desktop={this.context.isLarge}
          animation={PopoverAnimationVertical}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          targetOrigin={{ vertical: "top", horizontal: "left" }}
        >
          {this.props.customActions.map(action => (
            <MenuItem
              key={action.label}
              style={{ cursor: "pointer" }}
              primaryText={action.label}
              onClick={action.onClick}
            />
          ))}
        </IconMenu>
      );
      if (this.props.hideOrderButton) {
        if (this.props.buyingAdditionalTicketsFromMenuOnly) {
          content = [this._makeMenu(iconButtonElement)];
        } else {
          content = [additionalButton];
        }
      } else {
        content.push(additionalButton);
      }
    }

    return (
      <div style={{ position: "relative" }}>
        {this.state.externalTicketsOverlayVisible ? (
          <BaseOverlay onRequestClose={this._hideExternalTicketsOverlay}>
            <div style={styles.externalTicketsOverlay}>
              <h2>{this.props.event.name}</h2>
              <p>
                Tickets for this event are being sold on another site. By
                clicking the button below, you will be taken to the ticket sales
                website for {this.props.event.name}.
              </p>
              <div>
                <RaisedButton
                  label="Continue to site"
                  primary={true}
                  onClick={this.continueToExternalTickets}
                />
              </div>
              <div style={{ marginTop: 7 }}>
                <FlatButton
                  label="Cancel"
                  onClick={this._hideExternalTicketsOverlay}
                />
              </div>
              <div style={styles.externalTicketsUrlInfo}>
                External url:{" "}
                <a
                  style={styles.externalTicketsUrl}
                  onClick={this.continueToExternalTickets}
                >
                  {this.props.event.external_tickets_url}
                </a>
              </div>
            </div>
          </BaseOverlay>
        ) : null}
        {this.state.soldOutOverlayVisible ? (
          <BaseOverlay onRequestClose={this._hideSoldOutOverlay}>
            <h2 style={{ margin: "0.25em 0px 0.5em" }}>
              {this.props.event.name}
            </h2>
            <InjectedTenantHTML
              content={
                this.context.appSettings.mode === "box_office_only" ||
                this.state.boxOfficeOnlyVisible
                  ? this.context.tenant.box_office_only_message
                  : this.context.tenant.event_sold_out_message
              }
            />
          </BaseOverlay>
        ) : null}
        {this.state.preorderedOverlayVisible ? (
          <BaseOverlay
            onRequestClose={() =>
              this.setState({ preorderedOverlayVisible: false })
            }
          >
            <h2 style={{ margin: "0.25em 0px 0.5em" }}>
              {this.props.event.name}
            </h2>
            <div>
              This screening is not yet available for viewing. You will be able
              to view the screening here beginning{" "}
              {moment(this.props.event.start_time).format("llll")}.
            </div>
          </BaseOverlay>
        ) : null}
        <div
          style={
            !isLarge && !compact && !inline
              ? styles.multiContainerSmall
              : styles.multiContainer
          }
          className="eventive__ticket-button__container eventive__universal_ticket-button__container"
          data-button-mode={buttonEventMode}
        >
          {(!isLarge &&
          !compact &&
          !inline &&
          !AppUtil.isSundanceMobileMode(this.context.tenant)
            ? [
                <RaisedButton
                  key="back"
                  style={Object.assign(
                    {},
                    styles.button,
                    styles.scheduleButton
                  )}
                  label={
                    <span>
                      <span style={{ fontSize: "1.4em" }}>‹</span>{" "}
                      {this.props.backLabel ||
                        this.context.tenant.display_schedule}
                    </span>
                  }
                  onClick={() =>
                    this.props.onBackToSchedule
                      ? this.props.onBackToSchedule()
                      : this.context.router.push("/schedule")
                  }
                />
              ]
            : []
          ).concat(content)}
        </div>
      </div>
    );
  }
}

export default connectToStores(TicketButton);



// WEBPACK FOOTER //
// ./src/app/components/partials/ticket-button.jsx